// Fetch EVERY record from a paginated list service so a dropdown is never
// truncated by a fixed page `limit`. The per-request limit becomes irrelevant:
// we read `total`/`totalPages` from the first response and pull the rest.
//
// Usage:
//   const list = await fetchAllPages(
//     PatientListService.getOrderTypes,
//     (res) => res?.data?.orderTypes,
//   );
export async function fetchAllPages<T = any>(
  serviceFn: (params: any) => Promise<any>,
  extract: (res: any) => T[] | undefined | null,
  params: Record<string, any> = {},
  limit = 100,
): Promise<T[]> {
  const first = await serviceFn({ pageNumber: 1, limit, ...params });
  let all: T[] = extract(first) ?? [];

  const data = first?.data ?? {};
  let totalPages = Number(data?.totalPages);
  const total = Number(data?.total);
  if (!totalPages && total) totalPages = Math.ceil(total / limit);

  if (totalPages && totalPages > 1) {
    // Fetch the remaining pages in parallel and concatenate in order.
    const rest = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, i) =>
        serviceFn({ pageNumber: i + 2, limit, ...params }),
      ),
    );
    rest.forEach((r) => {
      all = all.concat(extract(r) ?? []);
    });
  } else if (!totalPages) {
    // Fallback when the API doesn't return total/totalPages: keep paging until
    // a short page comes back (with a hard safety cap).
    let page = 2;
    let lastLen = all.length;
    while (lastLen === limit && page <= 200) {
      const r = await serviceFn({ pageNumber: page, limit, ...params });
      const list = extract(r) ?? [];
      all = all.concat(list);
      lastLen = list.length;
      page += 1;
    }
  }

  return all;
}
